import { cva } from 'class-variance-authority';
import { GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import { PHASE_DEVELOPMENT_SERVER, PHASE_PRODUCTION_BUILD } from 'next/constants';
import { useTranslation } from 'next-i18next';

import { CHANNEL, paintguidePageSlugs } from '@boss/constants/b2b-b2c';
import {
  IEnrichedQuestion,
  color as colorService,
  content as contentService,
  paintsystem as paintsystemService,
} from '@boss/services';
import { Environment, PasQuestionFields } from '@boss/types/b2b-b2c';

import { SEO } from '../../components';
import { PageTypes } from '../../constants';
import { PaintguidePage } from '../../containers';
import { useColorGroup } from '../../hooks';
import Layout from '../../layout/default';
import { paintSystemTarget, paintguideEnrichedQuestionMap, stepperVariant } from '../../utils';
import getServerSideTranslations from '../../utils/getServerSideTranslations';
import { mapNextToContentfulLocale } from '../../utils/localeMapper';
import { getMappedNavigation } from '../../utils/navigation';

const IS_BUILD =
  (process.env.NEXT_PHASE === PHASE_PRODUCTION_BUILD && !process.env.SKIP_PRODUCT_BUILD) ||
  (process.env.NEXT_PHASE === PHASE_DEVELOPMENT_SERVER && process.env.DEBUG_BUILD === 'true');

export const getEnrichedQuestions = (pasQuestions: PasQuestionFields[] | null) =>
  pasQuestions?.reduce((questions: Record<string, IEnrichedQuestion>, question) => {
    const questionKey = paintguideEnrichedQuestionMap[question.propertyKeyId];
    const { enrichedQuestionText, extraInformation, shortEnrichedQuestionText } = question;

    const gVal = <T,>(val: T) => val ?? null;

    questions[questionKey] = {
      title: gVal(enrichedQuestionText),
      question: gVal(shortEnrichedQuestionText),
      extraInfo: gVal(extraInformation),
    };

    return questions;
  }, {});

export const getStaticProps = async ({ preview, locale }: GetStaticPropsContext) => {
  if (!locale || locale === 'default') {
    return {
      notFound: true,
    };
  }

  const contentfulLocale = mapNextToContentfulLocale(locale);

  /**
   * As we fetch facets from Algolia, we need a way to get all the enriched data for the facet options,
   * such as translations, image data, richtext, ...
   *
   * Currently there is no way to acces this info from algolia within a single request
   * we create a look up table/map to get this info, we pass this down to our components where further mapping happens
   * */
  const [navigation, paintSystemsOptionsMap, paintguideOverviewPage, colorGroups, pasQuestions] = await Promise.all([
    getMappedNavigation(contentfulLocale, !!preview),
    paintsystemService.getOptionsMap({
      locale: contentfulLocale,
      target: paintSystemTarget,
      batchMode: IS_BUILD,
    }),
    contentService.getOverviewPage({
      locale: contentfulLocale,
      channel: CHANNEL,
      type: 'Paint guide',
      batchMode: IS_BUILD,
      preview: !!preview,
    }),
    colorService.getColorGroups(contentfulLocale),
    paintsystemService.getEnrichedQuestions({
      locale: contentfulLocale,
      channel: CHANNEL,
      batchMode: IS_BUILD,
      preview: !!preview,
      questionFilter: Object.keys(paintguideEnrichedQuestionMap).join(','),
    }),
  ]);

  if (!navigation) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      pageType: PageTypes.PAINTGUIDE_OVERVIEW,
      enrichedQuestions: getEnrichedQuestions(pasQuestions) ?? {},
      paintSystemsOptionsMap,
      locale,
      preview: !!preview,
      navigation,
      paintguideOverviewPage,
      colorGroups,
      environment: process.env.CONTENTFUL_ENVIRONMENT as Environment,
      ...(await getServerSideTranslations(locale, ['common', 'paintguide', 'product', 'stores', 'forms'])),
    },
  };
};

const backgroundColor = cva('relative break-before break-after', {
  variants: {
    stepperVariant: {
      primary: 'bg-generic-blue-light before:bg-generic-blue-light after:bg-generic-blue-light',
      secondary: '',
    },
  },
});

export const Index = ({
  paintSystemsOptionsMap,
  navigation,
  preview,
  environment,
  locale,
  paintguideOverviewPage,
  colorGroups,
  enrichedQuestions,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { t } = useTranslation('paintguide');
  const pageTitle = t('paintguideLabel', { ns: 'paintguide' });
  const pageDescription = paintguideOverviewPage?.pageTitle || pageTitle;
  const crumbles = [{ href: paintguidePageSlugs[locale], label: pageTitle }];

  const { normalizeColorGroupNames } = useColorGroup();
  const normalizedColorGroups = normalizeColorGroupNames(colorGroups);

  return (
    <SEO
      breadcrumbs={crumbles}
      description={pageDescription}
      imageSrc={paintguideOverviewPage?.ogImage?.image?.file?.url}
      localizedSlugs={paintguidePageSlugs}
      navigation={navigation}
      title={pageTitle}
    >
      <Layout
        breadcrumbWrapperClassName={backgroundColor({ stepperVariant })}
        crumbles={crumbles}
        environment={environment}
        localizedSlugs={paintguidePageSlugs}
        navigation={navigation}
        preview={preview}
      >
        <PaintguidePage
          colorGroups={normalizedColorGroups}
          enrichedQuestions={enrichedQuestions ?? undefined}
          optionsMap={paintSystemsOptionsMap}
          pageTitle={pageTitle}
          paintguideOverviewPage={paintguideOverviewPage}
        />
      </Layout>
    </SEO>
  );
};

export default Index;
